import PropTypes from 'prop-types';
import React from 'react';
import classnames from 'classnames';
import { FeaturesReady, useFeatureValue } from '@growthbook/growthbook-react';

import useCurrentUser from 'dpl/common/hooks/useCurrentUser';
import {
  ADOPTER_APPLICATION_OPEN,
  LITTER_ID,
  PUPPY_ID
} from 'dpl/constants/query_params';
import { GB_FEATURES } from 'dpl/shared/constants/gb_features';
import {
  ANALYTICS_EVENTS,
  ANALYTICS_USER_JOURNEYS,
  ANALYTICS_VIEW_FEATURES
} from 'dpl/constants/analytics';
import { INITIAL_INQUIRIES_FIELD_NAME } from 'dpl/initial_inquiry/utils/constants';
import { INITIAL_INQUIRY_SOURCE_VIEWS } from 'dpl/initial_inquiry/utils/analytics';
import { addToQuery } from 'dpl/util/queryString';
import { sendRudderstackEvent } from 'dpl/util/analytics';
import { get } from 'dpl/shared/utils/object';
import { setAnalyticsOrigin } from 'dpl/shared/utils/setAnalyticsOrigin';
import { BUYER_MESSAGES_PATH } from 'dpl/shared/constants/urls';
import { openPuppyPage } from 'dpl/puppy_page/utils/helpers';
import PuppyCardTransport from 'dpl/puppy_search_results/components/PuppyCardTransport';
import { RESULTS_TYPES } from 'dpl/puppy_search_results/utils/constants';

import Transport from './Transport';

export default function CardFooter(props) {
  return (
    <FeaturesReady timeout={500}>
      <Component {...props} />
    </FeaturesReady>
  );
}

function Component({
  breederProfileUrl,
  isReservedPuppy,
  snoozed,
  deliveryMethods,
  displayApplyCta,
  breederProfileId,
  analytics,
  deliveryMethodsCount,
  puppyId,
  litterId,
  breederFirstName,
  facilityLocation,
  puppyName,
  view,
  cardType,
  distance
}) {
  const currentUser = useCurrentUser();

  const experimentVariant = useFeatureValue(
    GB_FEATURES.EXP_PROFILE_V_APP_3,
    'control'
  );

  function handleApplyClick(e) {
    e.preventDefault();
    e.stopPropagation();

    setAnalyticsOrigin({
      originViewFeature: analytics?.view_feature,
      originViewFeatureLocation: analytics?.view_feature_location
    });

    if (INITIAL_INQUIRY_SOURCE_VIEWS.includes(view)) {
      const initialInquiries = get(
        currentUser,
        INITIAL_INQUIRIES_FIELD_NAME,
        []
      );

      const initialInquiry = initialInquiries.find(
        inquiry => inquiry.breeder_profile_id === breederProfileId
      );

      sendRudderstackEvent(ANALYTICS_EVENTS.CARD_CLICKED_BY_USER, {
        ...analytics,
        litter_id: litterId,
        puppy_id: puppyId,
        view_feature: ANALYTICS_VIEW_FEATURES.REQUEST_INFO_BUTTON
      });

      if (initialInquiry) {
        window.location.href = `${BUYER_MESSAGES_PATH}/active/${
          initialInquiry.conversation_id || ''
        }`;
      } else if (experimentVariant === 'control') {
        window.location.href = `/puppy/${puppyId}/contacts/buyer_profile`;
      } else if (experimentVariant === 'buyer_application') {
        window.location.href = `/puppy/${puppyId}/contacts/buyer_application`;
      }
    } else {
      sendRudderstackEvent(ANALYTICS_EVENTS.BUYER_APP_STARTED, {
        ...analytics,
        buyer_profile_id: currentUser?.value?.data?.buyer_profile_id,
        user_journey: ANALYTICS_USER_JOURNEYS.BUYER_ACTIVATION
      });

      const searchParams = addToQuery(
        { [ADOPTER_APPLICATION_OPEN]: true },
        window.location.search
      );
      const [breederProfileEndpoint, hashParams] = breederProfileUrl.split('#');

      const updatedHashParams =
        puppyId && litterId
          ? addToQuery(
              { [LITTER_ID]: litterId, [PUPPY_ID]: puppyId },
              hashParams
            )
          : hashParams;

      window.location.href = `${breederProfileEndpoint}?${searchParams}${
        updatedHashParams ? `#${updatedHashParams}` : ''
      }`;
    }
  }

  function handleViewInfoClick(e) {
    e.preventDefault();
    e.stopPropagation();

    sendRudderstackEvent(ANALYTICS_EVENTS.CARD_CLICKED_BY_USER, {
      ...analytics,
      litter_id: litterId,
      puppy_id: puppyId,
      view_feature: ANALYTICS_VIEW_FEATURES.VIEW_DETAILS_BUTTON
    });

    if (puppyId) {
      openPuppyPage({ puppyId });
    } else {
      window.location.href = breederProfileUrl;
    }
  }

  return (
    <div
      className={classnames(
        'BreederSearchResultsCardFooter mh4 pv4 items-center f2',
        {
          'bt b--light-gray': !puppyId,
          'flex justify-between': !snoozed
        }
      )}
    >
      <div>
        {!snoozed && cardType === RESULTS_TYPES.BREEDERS && (
          <Transport
            deliveryMethods={deliveryMethods}
            deliveryMethodsCount={deliveryMethodsCount}
            breederFirstName={breederFirstName}
            facilityLocation={facilityLocation}
            puppyName={puppyName}
          />
        )}
        {!snoozed && cardType === RESULTS_TYPES.PUPPIES && (
          <PuppyCardTransport
            deliveryMethods={deliveryMethods}
            breederFirstName={breederFirstName}
            facilityLocation={facilityLocation}
            puppyName={puppyName}
            distance={distance}
          />
        )}
      </div>
      <CardCTA
        snoozed={snoozed}
        isReservedPuppy={isReservedPuppy}
        displayApplyCta={displayApplyCta}
        handleViewInfoClick={handleViewInfoClick}
        handleApplyClick={handleApplyClick}
      />
    </div>
  );
}

Component.propTypes = {
  breederProfileUrl: PropTypes.string.isRequired,
  snoozed: PropTypes.bool.isRequired,
  isReservedPuppy: PropTypes.bool,
  // eslint-disable-next-line react/forbid-prop-types
  deliveryMethods: PropTypes.object,
  displayApplyCta: PropTypes.bool.isRequired,
  breederProfileId: PropTypes.number.isRequired,
  analytics: PropTypes.shape({
    view: PropTypes.string.isRequired,
    view_feature: PropTypes.string,
    view_feature_location: PropTypes.string
  }),
  deliveryMethodsCount: PropTypes.number,
  puppyId: PropTypes.number,
  litterId: PropTypes.number,
  breederFirstName: PropTypes.string,
  facilityLocation: PropTypes.string,
  puppyName: PropTypes.string,
  view: PropTypes.string,
  cardType: PropTypes.string.isRequired,
  distance: PropTypes.number
};

Component.defaultProps = {
  deliveryMethods: {},
  analytics: {},
  deliveryMethodsCount: null,
  puppyId: null,
  litterId: null,
  isReservedPuppy: false,
  breederFirstName: null,
  facilityLocation: null,
  puppyName: null,
  view: '',
  distance: null
};

function CardCTA({
  snoozed,
  isReservedPuppy,
  displayApplyCta,
  handleViewInfoClick,
  handleApplyClick
}) {
  if (snoozed || !displayApplyCta) return null;

  if (isReservedPuppy) {
    return (
      <button
        type="button"
        data-test-id="breeder_card_view_breeder_link"
        className="box-shadow-hover bg-near-white b--light-gray ba br3 pv2 ph4 fw-medium nowrap"
        onClick={handleViewInfoClick}
      >
        View details
      </button>
    );
  }

  return (
    <button
      type="button"
      data-test-id="breeder_card_apply_button"
      className="box-shadow-hover bg-near-white b--light-gray ba br3 pv2 ph4 fw-medium nowrap ml2"
      onClick={e => handleApplyClick(e)}
    >
      Request info
    </button>
  );
}

CardCTA.propTypes = {
  snoozed: PropTypes.bool.isRequired,
  isReservedPuppy: PropTypes.bool.isRequired,
  displayApplyCta: PropTypes.bool.isRequired,
  handleViewInfoClick: PropTypes.func.isRequired,
  handleApplyClick: PropTypes.func.isRequired
};
